/**
 * LocalStorage 클래스
 *
 * 다중 탭 환경에서 세션을 관리하는 클래스입니다.
 *
 * [주요 기능]
 * 1. 각 탭별로 독립적인 세션 유지 (sessionStorage 활용)
 * 2. 브라우저 재시작 시 세션 복원 (localStorage에 백업)
 * 3. 여러 탭이 동시에 열릴 때 서로 다른 세션을 자동 할당 (BroadcastChannel 활용)
 *
 * [저장소 구조]
 * - sessionStorage: 현재 탭의 세션 정보 (Token, RefreshToken, StoreId, AccountId, TabId)
 * - localStorage.sessions: 모든 세션의 백업 (storeId를 키로 사용하는 객체)
 *   예: { "store123": { token, refreshToken, storeId, accountId }, "store456": { ... } }
 *
 * [동작 시나리오]
 * - Case 1 (SPA 내부 이동): isInitialized가 true면 기존 세션 유지
 * - Case 2 (새로고침): sessionStorage에 StoreId와 Token이 있으면 기존 세션 유지
 * - Case 3 (브라우저 재시작/새 탭): localStorage에서 사용 가능한 세션을 찾아 복원
 *
 * [다중 탭 세션 분배 로직]
 * - 새 탭이 열리면 tabId의 해시값으로 초기 세션을 선택 (분산 효과)
 * - BroadcastChannel을 통해 다른 탭들이 사용 중인 storeId를 수집
 * - 300ms 후 충돌이 있으면 다른 세션으로 변경, 없으면 초기 선택 유지
 */
class LocalStorage {

  constructor() {
    // 현재 탭의 고유 식별자 (UUID 형태)
    this.tabId = null;

    // 탭 간 통신을 위한 BroadcastChannel 인스턴스
    this.channel = null;

    // 현재 JS 세션에서 initSession이 완료되었는지 여부
    // SPA 내부 이동 시 재초기화를 방지하는 데 사용
    this.isInitialized = false;

    // 다른 탭의 REQUEST_ACTIVE_STOREID 요청에 응답 가능한 상태인지 여부
    // 세션 선택이 시작된 후에만 true가 됨
    this.sessionReady = false;

    // 현재 선택 중이거나 선택 예정인 storeId
    // 300ms 대기 중에도 다른 탭의 요청에 응답하기 위해 사용
    this.pendingStoreId = null;

    this._authChangeCallback = null;
  }

  onAuthChange(callback) {
    this._authChangeCallback = callback;
  }

  /**
   * 앱 시작 시 호출되는 세션 초기화 메서드
   *
   * 현재 상황에 따라 세 가지 케이스로 분기하여 처리합니다:
   * - Case 1: SPA 내부 이동 (기존 세션 유지)
   * - Case 2: 페이지 새로고침 (기존 세션 유지)
   * - Case 3: 브라우저 재시작 또는 새 탭 (세션 복원 시도)
   *
   * @param {Function} onComplete - 초기화 완료 후 호출될 콜백 함수
   */
  initSession(onComplete) {
    const existingTabId = window.sessionStorage.getItem('TabId');
    const existingToken = window.sessionStorage.getItem('Token');
    const existingStoreId = window.sessionStorage.getItem('StoreId');

    // ========== Case 1: SPA 내부 이동 ==========
    // 이미 현재 JS 세션에서 초기화가 완료된 경우
    // React Router 등으로 페이지 이동 시 이 케이스에 해당
    // BroadcastChannel만 재설정하고 기존 세션을 그대로 사용
    if (this.isInitialized && existingTabId) {
      this.tabId = existingTabId;
      this.setupBroadcastChannel();

      if (onComplete) onComplete();
      return;
    }

    // ========== Case 2: 페이지 새로고침 ==========
    // sessionStorage에 StoreId와 Token이 모두 있는 경우
    // 이는 이미 로그인된 상태에서 새로고침한 것이므로 세션을 그대로 유지
    // pendingStoreId를 설정하여 다른 탭이 같은 세션을 선택하지 않도록 함
    if (existingStoreId && existingToken) {
      this.tabId = existingTabId || crypto.randomUUID();
      window.sessionStorage.setItem('TabId', this.tabId);
      this.pendingStoreId = existingStoreId;
      this.setupBroadcastChannel();
      this.isInitialized = true;
      this.sessionReady = true;

      if (onComplete) onComplete();
      return;
    }

    // ========== Case 3: 브라우저 재시작 또는 새 탭 ==========
    // sessionStorage가 비어있거나 불완전한 경우
    // localStorage에서 사용 가능한 세션을 찾아 복원해야 함

    // 새로운 탭 ID 생성 및 저장
    this.tabId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = Math.random() * 16 | 0;
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    window.sessionStorage.setItem('TabId', this.tabId);

    // 브라우저 재시작으로 인해 sessionStorage에 남아있는 stale 데이터 제거
    // (브라우저가 sessionStorage를 복원하는 경우가 있음)
    if (existingStoreId) {
      window.sessionStorage.removeItem('Token');
      window.sessionStorage.removeItem('RefreshToken');
      window.sessionStorage.removeItem('StoreId');
      window.sessionStorage.removeItem('AccountId');
    }

    this.setupBroadcastChannel();
    this.isInitialized = true;

    // 다른 탭과 협의하여 사용 가능한 세션을 할당받고 복원
    this.assignSessionAndRestore(onComplete);
  }

  /**
   * BroadcastChannel 설정
   *
   * 탭 간 통신을 위한 채널을 생성하고 메시지 핸들러를 등록합니다.
   * 다른 탭이 REQUEST_ACTIVE_STOREID를 보내면 현재 사용 중인 storeId로 응답합니다.
   *
   * 이미 채널이 설정되어 있거나 BroadcastChannel을 지원하지 않는 브라우저에서는
   * 아무 작업도 수행하지 않습니다.
   */
  setupBroadcastChannel() {
    // 이미 채널이 설정된 경우 중복 설정 방지
    if (this.channel) {
      return;
    }

    // BroadcastChannel 미지원 브라우저 체크 (IE, 일부 구형 브라우저)
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    this.channel = new BroadcastChannel('link_zone_session');

    this.channel.onmessage = (event) => {
      // 다른 탭이 사용 중인 storeId를 요청하는 메시지 처리
      if (event.data.type === 'REQUEST_ACTIVE_STOREID') {
        // 아직 세션 선택이 완료되지 않은 경우 응답하지 않음
        // (초기화 중인 탭이 잘못된 정보를 보내는 것을 방지)
        if (!this.sessionReady) {
          return;
        }

        // sessionStorage의 StoreId가 있으면 사용, 없으면 pendingStoreId 사용
        // (세션 선택 후 아직 sessionStorage에 저장하기 전일 수 있음)
        const myStoreId = window.sessionStorage.getItem('StoreId') || this.pendingStoreId;

        this.channel.postMessage({
          type: 'REPORT_ACTIVE_STOREID',
          storeId: myStoreId,
          targetId: event.data.senderId,
          fromTabId: this.tabId
        });
      }
    };
  }

  /**
   * 세션 할당 및 복원
   *
   * localStorage에 저장된 세션들 중 다른 탭이 사용하지 않는 세션을 찾아 복원합니다.
   *
   * [동작 순서]
   * 1. tabId 해시값을 이용해 초기 세션을 선택 (동시 시작 시 분산 효과)
   * 2. pendingStoreId 설정 후 sessionReady를 true로 변경 (다른 탭 응답 가능)
   * 3. BroadcastChannel로 다른 탭들의 사용 중인 storeId를 요청
   * 4. 300ms 후 수집된 정보를 바탕으로 최종 세션 결정
   *    - 초기 선택한 세션이 충돌하지 않으면 그대로 사용
   *    - 충돌하면 사용되지 않는 다른 세션을 찾아 사용
   *
   * @param {Function} onComplete - 복원 완료 후 호출될 콜백 함수
   */
  assignSessionAndRestore(onComplete) {
    const senderId = this.tabId;
    const activeStoreIds = new Set();

    // localStorage에서 저장된 세션 목록 조회
    const sessions = this.getSessions();
    const sessionKeys = Object.keys(sessions).sort((a, b) => Number(a) - Number(b));

    // 저장된 세션이 없으면 복원할 것이 없음
    if (sessionKeys.length === 0) {
      this.sessionReady = true;
      if (onComplete) onComplete();
      return;
    }

    // ========== 1단계: 초기 세션 선택 (해시 기반 분산) ==========
    // tabId의 문자 코드 합을 해시값으로 사용
    // 여러 탭이 동시에 시작해도 서로 다른 세션을 선택할 확률이 높아짐
    const tabIdHash = this.tabId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const initialIndex = tabIdHash % sessionKeys.length;

    this.pendingStoreId = sessionKeys[initialIndex];

    // 이 시점부터 다른 탭의 요청에 응답 가능
    // (pendingStoreId로 응답하여 다른 탭이 같은 세션을 피할 수 있게 함)
    this.sessionReady = true;

    // BroadcastChannel이 없는 환경에서는 바로 복원 진행
    if (!this.channel) {
      this.restoreSession(sessions[this.pendingStoreId]);
      if (onComplete) onComplete();
      return;
    }

    // ========== 2단계: 다른 탭들의 사용 중인 storeId 수집 ==========
    const handleResponse = (event) => {
      if (event.data.type === 'REPORT_ACTIVE_STOREID' && event.data.targetId === senderId) {
        // 자기 자신의 응답은 무시
        if (event.data.fromTabId === senderId) {
          return;
        }

        if (event.data.storeId) {
          activeStoreIds.add(event.data.storeId);
        }
      }
    };

    this.channel.addEventListener('message', handleResponse);

    // 다른 탭들에게 사용 중인 storeId 요청
    this.channel.postMessage({
      type: 'REQUEST_ACTIVE_STOREID',
      senderId: senderId
    });

    // ========== 3단계: 300ms 후 최종 세션 결정 ==========
    // 충분한 시간을 두어 다른 탭들의 응답을 기다림
    setTimeout(() => {
      this.channel.removeEventListener('message', handleResponse);

      let sessionToRestore = null;
      let sessionKey = null;

      // 초기 선택한 세션이 다른 탭과 충돌하는지 확인
      if (this.pendingStoreId && !activeStoreIds.has(this.pendingStoreId)) {
        // 충돌 없음 - 초기 선택 유지
        sessionKey = this.pendingStoreId;
        sessionToRestore = sessions[sessionKey];
      }
      else {
        // 충돌 발생 - 사용되지 않는 다른 세션 찾기
        for (let i = 0; i < sessionKeys.length; i++) {
          const key = sessionKeys[i];

          if (!activeStoreIds.has(key)) {
            sessionKey = key;
            sessionToRestore = sessions[key];
            break;
          }
        }
      }

      // 사용 가능한 세션을 찾은 경우 복원
      if (sessionToRestore) {
        this.restoreSession(sessionToRestore);
        this.pendingStoreId = sessionKey;
      }
      else {
        // 모든 세션이 사용 중이거나 세션이 없음
        this.pendingStoreId = null;
      }

      if (onComplete) onComplete();
    }, 300);
  }

  /**
   * localStorage에서 sessions 객체 조회
   *
   * @returns {Object} storeId를 키로 하는 세션 객체들의 맵
   *                   예: { "store123": { token, refreshToken, storeId, accountId } }
   */
  getSessions() {
    const sessionsStr = window.localStorage.getItem('sessions');

    if (!sessionsStr) return {};

    try {
      return JSON.parse(sessionsStr);
    }
    catch (e) {
      return {};
    }
  }

  /**
   * localStorage에 sessions 객체 저장
   *
   * @param {Object} sessions - 저장할 세션 객체들의 맵
   */
  setSessions(sessions) {
    window.localStorage.setItem('sessions', JSON.stringify(sessions));
  }

  /**
   * 세션 데이터를 sessionStorage에 복원
   *
   * localStorage에서 가져온 세션 데이터를 현재 탭의 sessionStorage에 설정합니다.
   *
   * @param {Object} sessionData - 복원할 세션 데이터
   * @param {string} sessionData.token - 액세스 토큰
   * @param {string} sessionData.refreshToken - 리프레시 토큰
   * @param {string} sessionData.storeId - 스토어 ID
   * @param {string} sessionData.accountId - 계정 ID
   */
  restoreSession(sessionData) {
    if (sessionData.token) {
      window.sessionStorage.setItem('Token', sessionData.token);
    }

    if (sessionData.refreshToken) {
      window.sessionStorage.setItem('RefreshToken', sessionData.refreshToken);
    }

    if (sessionData.storeId) {
      window.sessionStorage.setItem('StoreId', sessionData.storeId);
    }

    if (sessionData.accountId) {
      window.sessionStorage.setItem('AccountId', sessionData.accountId);
    }
  }

  /**
   * 현재 탭의 세션을 localStorage에 백업 저장
   *
   * sessionStorage의 세션 정보를 localStorage의 sessions 객체에 저장합니다.
   * storeId를 키로 사용하여 동일 계정의 세션은 덮어씁니다.
   * 브라우저를 닫았다 다시 열어도 세션을 복원할 수 있게 합니다.
   */
  saveSession() {
    const storeId = window.sessionStorage.getItem('StoreId');

    if (!storeId) {
      return;
    }

    const sessions = this.getSessions();

    const newSessionData = {
      token: window.sessionStorage.getItem('Token'),
      refreshToken: window.sessionStorage.getItem('RefreshToken'),
      storeId: storeId,
      accountId: window.sessionStorage.getItem('AccountId')
    };

    // storeId를 키로 저장 (동일 storeId면 덮어씀)
    sessions[storeId] = newSessionData;

    this.setSessions(sessions);
    if (this._authChangeCallback) this._authChangeCallback();
  }

  /**
   * 토큰을 sessionStorage에 저장하고 localStorage에 백업
   *
   * @param {string} token - 저장할 액세스 토큰
   */
  setToken(token) {
    window.sessionStorage.setItem('Token', token);
    this.saveSession();
  }

  /**
   * 리프레시 토큰을 sessionStorage에 저장하고 localStorage에 백업
   *
   * @param {string} refreshToken - 저장할 리프레시 토큰
   */
  setRefreshToken(refreshToken) {
    window.sessionStorage.setItem('RefreshToken', refreshToken);
    this.saveSession();
  }

  /**
   * 스토어 ID를 sessionStorage에 저장하고 localStorage에 백업
   *
   * @param {string} storeId - 저장할 스토어 ID
   */
  setStoreId(storeId) {
    window.sessionStorage.setItem('StoreId', storeId);
    this.saveSession();
  }

  /**
   * sessionStorage에서 액세스 토큰 조회
   *
   * @returns {string|null} 액세스 토큰 또는 null
   */
  getToken() {
    return window.sessionStorage.getItem('Token');
  }

  /**
   * sessionStorage에서 리프레시 토큰 조회
   *
   * @returns {string|null} 리프레시 토큰 또는 null
   */
  getRefreshToken() {
    return window.sessionStorage.getItem('RefreshToken');
  }

  /**
   * sessionStorage에서 스토어 ID 조회
   *
   * @returns {string|null} 스토어 ID 또는 null
   */
  getStoreId() {
    return window.sessionStorage.getItem('StoreId');
  }

  /**
   * 현재 탭의 세션만 로그아웃 (단일 세션 제거)
   *
   * sessionStorage를 클리어하고 localStorage의 sessions에서
   * 해당 storeId의 세션만 삭제합니다.
   * 다른 탭의 세션이나 다른 계정의 세션은 유지됩니다.
   */
  clearSession() {
    const storeId = window.sessionStorage.getItem('StoreId');

    window.sessionStorage.removeItem('Token');
    window.sessionStorage.removeItem('RefreshToken');
    window.sessionStorage.removeItem('StoreId');
    window.sessionStorage.removeItem('AccountId');

    // localStorage sessions에서 해당 storeId 세션만 삭제
    if (storeId) {
      const sessions = this.getSessions();
      delete sessions[storeId];
      this.setSessions(sessions);
    }
  }

  /**
   * 완전 로그아웃 (모든 세션 제거)
   *
   * 현재 탭의 sessionStorage와 localStorage의 모든 sessions를 삭제합니다.
   * 모든 계정에서 완전히 로그아웃하고 싶을 때 사용합니다.
   */
  clearAll() {
    window.sessionStorage.removeItem('Token');
    window.sessionStorage.removeItem('RefreshToken');
    window.sessionStorage.removeItem('StoreId');
    window.sessionStorage.removeItem('AccountId');

    // 모든 세션 제거
    window.localStorage.removeItem('sessions');
  }
}

export default new LocalStorage();
